import { useState } from "react";
import { Icon } from "./Icon";
import {
  ESCALATION_COLORS,
  ESCALATION_LINKS,
  donutArcs,
  first,
  hoursToHM,
  npsScore,
  needsFollowUp,
  ratedLevel,
  ratio,
  scoreColor,
  ringDash,
  volumeSeries,
  wouldRecommend,
} from "../../lib/msdAnalytics";
import Dropdown from "../Dropdown";

// The Workshop analytics cards, rebuilt from design/maruti-service-desk.html.
// Presentational: the page hands each one its numbers. Every class value is
// the prototype's final computed value.

export const AN_CARD = "min-w-0 rounded-[20px] bg-msd-surface p-6 shadow-msd-e2 max-[640px]:p-4";
export const AN_SUB = "text-[13px] text-msd-muted";
export const AN_H = "mb-[6px] flex flex-wrap items-start justify-between gap-3";
export const AN_T = "text-[16px] font-semibold tracking-[-0.01em]";
const AN_SEC = "mb-3 mt-2 text-[16px] font-semibold tracking-[-0.01em]";
export const AN_NUM = "mb-[2px] mt-1 text-[34px] font-semibold leading-[1.1] tracking-[-0.03em] max-[640px]:text-[28px]";
export const AN_NUM_SM = "mb-[2px] mt-1 text-[26px] font-semibold leading-[1.1] tracking-[-0.03em]";
const AN_BIG = "min-w-[34px] text-right text-[26px] font-bold tracking-[-0.02em]";
const AN_BIG_SM = "min-w-[34px] text-right text-[20px] font-bold tracking-[-0.02em]";
const AN_LI =
  "flex w-full items-center gap-[14px] rounded-none border-0 border-t border-solid border-msd-line bg-transparent px-1 py-3 text-left hover:bg-msd-soft";
const AN_LI_TIGHT =
  "flex w-full items-center gap-3 rounded-none border-0 border-t border-solid border-msd-line bg-transparent px-1 py-[10px] text-left first:border-t-0 hover:bg-msd-soft";
const AN_DONUT = "mt-[10px] flex items-center gap-6 max-[640px]:flex-col max-[640px]:items-start";
const DISP = "tracking-[-0.02em]";

export const Unit = ({ children }) => (
  <span className="ml-0.5 text-[.5em] font-semibold tracking-[0] text-msd-muted">{children}</span>
);

// "5h 44m" as the big number with small units.
const HM = ({ value }) =>
  value ? (
    <>
      {value.h}
      <Unit>h</Unit> {value.m}
      <Unit>m</Unit>
    </>
  ) : (
    "—"
  );

export function Ring({ cx, r, w, frac, color }) {
  const { circumference, dash } = ringDash(r, frac);
  return (
    <>
      <circle cx={cx} cy={cx} r={r} fill="none" stroke={color} strokeOpacity=".16" strokeWidth={w} />
      <circle
        cx={cx}
        cy={cx}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={w}
        strokeLinecap="round"
        strokeDasharray={`${dash.toFixed(1)} ${circumference.toFixed(1)}`}
        transform={`rotate(-90 ${cx} ${cx})`}
      />
    </>
  );
}

const Dot = ({ color }) => <span className="inline-block h-[10px] w-[10px] shrink-0 rounded-full" style={{ background: color }} />;

// Segmented control: `.seg2`.
export function Seg({ options, value, onChange, label }) {
  return (
    <div className="inline-flex h-9 max-w-full items-center overflow-x-auto rounded-[10px] bg-msd-soft p-[3px]" role="group" aria-label={label}>
      {options.map(([k, text]) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          onClick={() => onChange(k)}
          data-testid={`msd-seg-${k}`}
          className={`h-7 shrink-0 whitespace-nowrap rounded-[7px] px-3 text-[13px] ${
            value === k ? "bg-msd-surface font-semibold text-msd-ink shadow-[0_1px_2px_rgba(16,24,40,.10),0_0_0_1px_rgba(16,24,40,.06)]" : "font-medium text-msd-muted hover:text-msd-ink"
          }`}
        >
          {text}
        </button>
      ))}
    </div>
  );
}

// Page title, then one row of filters under it: dates first, then where.
export function PageHead({ range, children, title = "Workshop analytics", description, divider = true }) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className={`m-0 text-[24px] font-semibold leading-8 ${DISP}`}>{title}</h1>
        {description ? <p className="mt-1 text-[14px] text-msd-muted">{description}</p> : null}
      </div>
      <div className={`flex flex-wrap items-center gap-2 ${divider ? "border-b border-msd-line pb-5" : ""}`}>
        {range}
        {children}
      </div>
    </div>
  );
}

export function SelectBox({ value, onChange, label, children, testid }) {
  return (
    <label>
      <Dropdown
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        data-testid={testid}
        className="h-9 cursor-pointer rounded-[10px] border border-msd-line bg-msd-surface pl-3 pr-8 text-[13px] font-medium text-msd-ink hover:border-msd-line2"
      >
        {children}
      </Dropdown>
    </label>
  );
}

export function EmptyScope({ text, onSetup }) {
  return (
    <div className={`${AN_CARD} !px-6 !py-12 text-center`}>
      <div className="text-[20px] font-semibold">Nothing here yet</div>
      <div className={`${AN_SUB} mx-0 mb-4 mt-[6px] !text-[15px]`}>{text}</div>
      <button
        type="button"
        onClick={onSetup}
        className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-msd-surface px-[14px] text-[14px] font-semibold shadow-msd-e1"
      >
        Set up a service journey
      </button>
    </div>
  );
}

// Everything the cars did this period in one card: the rings and key figures on
// top, and under them a per-day chart of whichever figure is picked. Four of the
// figures are buttons; the two timings are not.
const FLOW_METRICS = {
  groups: { unit: "service groups created", color: "var(--pd-fill)" },
  jobs: { unit: "job cards opened", color: "#6B8FC2" },
  repaired: { unit: "cars repaired", color: "#6B8FC2" },
  delivered: { unit: "cars delivered", color: "#17B26A" },
};

function FlowPick({ metric, current, onPick, children, className = "" }) {
  const on = current === metric;
  return (
    <button
      type="button"
      aria-pressed={on}
      title={`Show ${FLOW_METRICS[metric].unit} per day`}
      data-testid={`msd-flow-${metric}`}
      onClick={() => onPick(metric)}
      className={`-m-[10px] block rounded-[14px] border-0 bg-transparent p-[10px] text-left font-[inherit] text-[inherit] hover:bg-msd-soft ${
        on ? "bg-msd-soft shadow-[inset_0_0_0_2px_var(--royal)]" : ""
      } ${className}`}
    >
      {children}
    </button>
  );
}

export function FlowCard({
  cars,
  jobs,
  delivered,
  repaired,
  repairHours,
  cycleHours,
  sinceLabel,
  metric,
  onMetric,
  series,
  average,
  granularity,
  onGranularity,
}) {
  const H = 170;
  const cur = FLOW_METRICS[metric] || FLOW_METRICS.groups;
  const bars = volumeSeries(series, granularity);
  const mx = Math.max(1, ...bars.map((x) => x.v));
  const avgV = granularity === "week" ? average * 7 : average;
  const total = series.reduce((a, p) => a + (p.count || 0), 0);
  const rings = [
    ["delivered", "Delivered", "#17B26A", delivered, "cars delivered", 80],
    ["repaired", "Repaired", "#6B8FC2", repaired, "cars repaired", 56],
  ];
  return (
    <section className={AN_CARD} data-testid="msd-flow">
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-10 max-[1150px]:grid-cols-1">
        <div className="flex items-center gap-[26px] max-[640px]:flex-col max-[640px]:items-start">
          <svg
            width="188"
            height="188"
            viewBox="0 0 188 188"
            role="img"
            aria-label={rings.map((r) => `${r[1]} ${r[3]} of ${cars}`).join(", ")}
          >
            {rings.map(([k, , c, n, , radius]) => (
              <Ring key={k} cx={94} r={radius} w={18} frac={ratio(n, cars)} color={c} />
            ))}
          </svg>
          <div className="flex flex-col gap-3">
            {rings.map(([k, l, c, n, cap]) => (
              <FlowPick key={k} metric={k} current={metric} onPick={onMetric}>
                <div className="text-[14px] font-bold" style={{ color: c }}>
                  {l}
                </div>
                <div className={AN_NUM_SM}>
                  {n}
                  <Unit>/{cars}</Unit>
                </div>
                <div className={AN_SUB}>{cap}</div>
              </FlowPick>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-[22px] border-l border-msd-line pl-9 max-[1150px]:border-l-0 max-[1150px]:border-t max-[1150px]:pl-0 max-[1150px]:pt-5">
          <FlowPick metric="groups" current={metric} onPick={onMetric}>
            <div className={AN_SUB}>Service groups</div>
            <div className={AN_NUM}>{cars}</div>
          </FlowPick>
          <FlowPick metric="jobs" current={metric} onPick={onMetric}>
            <div className={AN_SUB}>Job cards</div>
            <div className={AN_NUM}>{jobs}</div>
          </FlowPick>
          <div title="Average time from the group being created to the car being ready" data-testid="msd-flow-tat">
            <div className={AN_SUB}>Total repair TAT</div>
            <div className={AN_NUM}>
              <HM value={hoursToHM(repairHours)} />
            </div>
          </div>
          <div title="Average time from the group being created to it being closed after delivery" data-testid="msd-flow-cycle">
            <div className={AN_SUB}>Group closure cycle time</div>
            <div className={AN_NUM}>
              <HM value={hoursToHM(cycleHours)} />
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 border-0 border-t border-solid border-msd-line pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-[15px] text-msd-muted" data-testid="msd-flow-heading">
            {granularity === "week" ? (
              <>
                <b className="mr-1 text-[22px] font-bold text-msd-ink">{total}</b> {cur.unit} since {sinceLabel}
              </>
            ) : (
              <>
                <b className="mr-1 text-[22px] font-bold text-msd-ink">{average.toFixed(1)}</b> {cur.unit} in a day since {sinceLabel}
              </>
            )}
          </div>
          <Seg
            label="Bar width"
            value={granularity}
            onChange={onGranularity}
            options={[
              ["day", "Day"],
              ["week", "Week"],
            ]}
          />
        </div>
        <div className="mt-[18px]">
          <div className="relative flex h-[170px] items-end gap-2 pr-[34px]">
            <div
              className="absolute left-0 right-[34px] z-[1] border-t-[1.5px] border-dashed border-[#17B26A]"
              style={{ bottom: `${Math.min(H, (avgV / mx) * H)}px` }}
            >
              <span
                className="absolute -top-[22px] left-0 z-[2] rounded-md bg-msd-surface px-[6px] py-px text-[11.5px] font-semibold text-[#17B26A]"
                title={`Average ${cur.unit} a ${granularity === "week" ? "week" : "day"} over the whole period, counting days with none`}
                data-testid="msd-flow-avg"
              >
                avg {Number.isInteger(avgV) ? avgV : avgV.toFixed(1)}
              </span>
            </div>
            <span className="absolute right-0 translate-y-1/2 text-[11.5px] text-msd-muted" style={{ bottom: `${H}px` }}>
              {mx}
            </span>
            <span className="absolute right-0 translate-y-1/2 text-[11.5px] text-msd-muted" style={{ bottom: 0 }}>
              0
            </span>
            {bars.map((x, i) => (
              <div
                key={i}
                className="flex h-full min-w-0 flex-1 basis-0 items-end justify-center"
                title={`${x.full}: ${x.v} ${cur.unit}`}
              >
                <span
                  className="block w-[min(16px,80%)] rounded-full"
                  style={{ height: `${x.v ? Math.max(8, (x.v / mx) * H) : 4}px`, background: x.v ? cur.color : "var(--line)" }}
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-2 pr-[34px]">
            {bars.map((x, i) => (
              <span key={i} className="flex-1 basis-0 text-center text-[12px] text-msd-muted">
                {x.lab}
              </span>
            ))}
          </div>
        </div>
        <div className={`${AN_SUB} mt-[10px]`}>Tap any number above to chart it by day.</div>
      </div>
    </section>
  );
}

export function Highlights({ items }) {
  return (
    <section>
      <div className={AN_SEC}>Highlights</div>
      <div className="grid grid-cols-3 gap-4 max-[1150px]:grid-cols-1">
        {items.map(({ color, icon, title, sub, cta, onClick, testid }) => (
          <button
            key={testid}
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`${AN_CARD} flex cursor-pointer flex-col gap-[6px] border-0 !p-5 text-left hover:shadow-[0_0_0_.5px_rgba(16,24,40,.06),0_4px_10px_rgba(16,24,40,.08),0_16px_32px_rgba(16,24,40,.08)]`}
          >
            <span className="mb-1 flex" style={{ color }}>
              <Icon name={icon} size={20} stroke={2} />
            </span>
            <span className="text-[17px] font-semibold leading-[1.3] tracking-[-0.01em]">{title}</span>
            <span className={AN_SUB}>{sub}</span>
            <span className="mt-[6px] flex items-center gap-1 text-[13.5px] font-semibold text-[#3A5F94]">
              {cta} <Icon name="right" size={14} stroke={2.2} />
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

// The list that opens when you hover (or focus) a figure: who is behind the
// number. Clicking someone opens their group.
function RespondentPop({ title, sub, people, chip, onOpen, align = "right", note, empty = "Nobody here yet." }) {
  const pos = align === "center" ? "left-1/2 -translate-x-1/2" : align === "left" ? "left-0" : "right-0";
  return (
    <div
      role="dialog"
      aria-label={title}
      data-testid="msd-respondents"
      className={`invisible absolute top-full z-[60] w-[340px] max-w-[86vw] pt-2 opacity-0 transition-[opacity,visibility] duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 ${pos}`}
    >
      <div className="rounded-2xl bg-msd-surface px-2 pb-2 pt-3 text-left shadow-msd-pop">
        <div className="flex flex-col px-[10px] pb-2">
          <b className="text-[15px] text-msd-ink">{title}</b>
          <span className={AN_SUB}>{sub}</span>
        </div>
        <div className="flex max-h-[300px] flex-col overflow-y-auto">
          {people.length === 0 && <div className={`${AN_SUB} px-[10px] py-3`}>{empty}</div>}
          {people.map((p) => {
            const [label, color] = chip(p);
            return (
              <button
                key={p.group_row_id}
                type="button"
                onClick={() => onOpen(p)}
                data-testid="msd-respondent"
                className="flex w-full cursor-pointer items-center gap-[10px] rounded-[10px] border-0 bg-transparent px-[10px] py-2 text-left font-[inherit] hover:bg-msd-soft focus-visible:bg-msd-soft"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-msd-soft text-[12px] font-bold text-msd-ink2">
                  {first(p.name)}
                </span>
                <span className="min-w-0 grow">
                  <b className="block truncate text-[14.5px] font-semibold text-msd-ink">{p.name || "Customer"}</b>
                  <span className={`${AN_SUB} block truncate`}>
                    {[p.plate, p.model, p.advisor].filter(Boolean).join(" · ")}
                  </span>
                </span>
                <span className="inline-flex shrink-0 items-center gap-[6px] rounded-full bg-msd-soft px-[9px] py-[3px] text-[12.5px] font-semibold text-msd-ink">
                  {color && <Dot color={color} />}
                  {label}
                </span>
                <Icon name="right" size={14} stroke={2.2} color="var(--faint)" />
              </button>
            );
          })}
        </div>
        {people.length > 0 && (
          <div className={`${AN_SUB} mt-1 border-0 border-t border-solid border-msd-line px-[10px] pb-[2px] pt-2`}>
            Click a customer to open their group{note ? ` · ${note}` : ""}
          </div>
        )}
      </div>
    </div>
  );
}

// What the customer rated the delivery: the same four words as the bars above.
const RATING_LABELS = { excellent: "Excellent", "very good": "Very good", good: "Good", bad: "Bad" };
const ratingChip = (p) => [RATING_LABELS[p.level] || "", FEEDBACK_COLORS[p.level] || null];

export function NpsCard({ nps, people = [], onOpen = () => {} }) {
  const total = nps.promoters + nps.passives + nps.detractors;
  const reach = nps.passives + nps.detractors;
  const score = npsScore(nps);
  const groups = [
    ["recommend", "Would recommend", "Scored 9 or 10", "#17B26A", nps.promoters, wouldRecommend(people)],
    ["follow-up", "Needs follow-up", "Scored 8 or below", "#F79009", reach, needsFollowUp(people)],
  ];
  return (
    <section className={AN_CARD} data-testid="msd-nps">
      <div className={AN_H}>
        <div>
          <div className={AN_T}>Net Promoter Score</div>
          <div className={AN_SUB}>{total} responses · hover a group to see who</div>
        </div>
      </div>
      <div className={AN_DONUT}>
        <div className="relative h-[150px] w-[150px] shrink-0">
          <svg width="150" height="150" viewBox="0 0 150 150" role="img" aria-label={`${nps.promoters} of ${total} customers scored 9 or 10`}>
            <Ring cx={75} r={62} w={16} frac={ratio(nps.promoters, total)} color="#17B26A" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className={AN_NUM_SM}>
              {score > 0 ? "+" : ""}
              {score}
            </div>
            <div className={AN_SUB}>score</div>
          </div>
        </div>
        <div className="min-w-0 grow">
          {groups.map(([key, label, cap, color, count, list]) => (
            <div key={key} className="group relative outline-none" tabIndex={0} data-testid={`msd-nps-${key}`}>
              <div className={`${AN_LI_TIGHT} cursor-default rounded-xl group-hover:bg-msd-soft group-focus-within:bg-msd-soft`}>
                <Dot color={color} />
                <span className="grow">
                  <b className="font-semibold">{label}</b>
                  <span className={`${AN_SUB} block`}>{cap}</span>
                </span>
                <span className={AN_BIG_SM}>{count}</span>
                <Icon name="right" size={14} stroke={2.2} color="var(--faint)" />
              </div>
              <RespondentPop
                title={label}
                sub={`${count} customer${count === 1 ? "" : "s"} · ${cap.toLowerCase()}`}
                people={list}
                chip={(p) => [String(p.score), scoreColor(p.score)]}
                onOpen={onOpen}
                note={list.length < count ? `showing the latest ${list.length}` : ""}
                empty={
                  total === 0
                    ? "No scores yet. Customers are asked to rate us from 1 to 10 after delivery."
                    : "Nobody in this group."
                }
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const FEEDBACK_COLORS = { excellent: "#17B26A", "very good": "#6B8FC2", good: "#F79009", bad: "#F04438" };

export function FeedbackCard({ items, people = [], onOpen = () => {} }) {
  const total = items.reduce((a, x) => a + x.count, 0);
  const mx = Math.max(1, ...items.map((x) => x.count));
  const bad = items.find((x) => x.key === "bad")?.count || 0;
  const good = (items.find((x) => x.key === "excellent")?.count || 0) + (items.find((x) => x.key === "very good")?.count || 0);
  const alignFor = (i) => (i === 0 ? "left" : i >= 2 ? "right" : "center");
  return (
    <section className={AN_CARD} data-testid="msd-feedback">
      <div className={AN_H}>
        <div>
          <div className={AN_T}>Service feedback at delivery</div>
          <div className={AN_SUB}>{total} responses · hover a bar to see who</div>
        </div>
      </div>
      <div className="mt-[18px] grid grid-cols-4 items-end gap-3">
        {items.map((x, i) => {
          const list = ratedLevel(people, x.key);
          return (
            <div
              key={x.key}
              className="group relative flex flex-col items-center rounded-xl py-[6px] text-center outline-none hover:bg-msd-soft focus-within:bg-msd-soft"
              tabIndex={0}
              data-testid={`msd-feedback-${x.key.replace(" ", "-")}`}
            >
              <div className="text-[30px] font-bold leading-none tracking-[-0.02em]">{x.count}</div>
              <div className="mb-[10px] mt-1 text-[12.5px] text-msd-muted">{total ? Math.round((x.count / total) * 100) : 0}%</div>
              <div className="flex h-[120px] items-end">
                <span
                  className="block w-[22px] rounded-full"
                  style={{ height: `${Math.max(8, (x.count / mx) * 120)}px`, background: FEEDBACK_COLORS[x.key] }}
                />
              </div>
              <div className="mt-[10px] text-[13px] font-semibold leading-[1.25]">{x.label}</div>
              <RespondentPop
                title={x.label}
                sub={`${x.count} customer${x.count === 1 ? "" : "s"} rated the service ${x.label.toLowerCase()}`}
                people={list}
                chip={ratingChip}
                onOpen={onOpen}
                align={alignFor(i)}
                note={list.length < x.count ? `showing the latest ${list.length}` : ""}
                empty="Nobody rated the service this way."
              />
            </div>
          );
        })}
      </div>
      {total > 0 && (
        <div className="mt-[18px] flex items-start gap-2 rounded-xl bg-msd-soft px-3 py-[10px] text-[13.5px] text-msd-ink2">
          <span className="mt-px shrink-0 text-[#3A5F94]">
            <Icon name="info" size={16} stroke={2} />
          </span>
          <span>
            {bad > 0 && (
              <>
                {bad} customer{bad === 1 ? "" : "s"} said needs improvement.{" "}
              </>
            )}
            {Math.round((good / total) * 100)}% rated the service very good or excellent.
          </span>
        </div>
      )}
    </section>
  );
}

export function WaitingCard({ advisors, managers, onOpenAdvisor }) {
  const [role, setRole] = useState("sa");
  const rows = role === "sa" ? advisors : managers.filter((m) => m.unanswered > 0);
  return (
    <section className={AN_CARD} data-testid="msd-waiting">
      <div className={AN_H}>
        <div className={AN_T}>Customer messages waiting for a reply</div>
        <Seg
          label="Who"
          value={role}
          onChange={setRole}
          options={[
            ["sa", "Advisors"],
            ["wm", "Managers"],
          ]}
        />
      </div>
      {role === "wm" && !rows.length ? (
        <div className={`${AN_SUB} px-0 py-7 text-center !text-[15px]`}>No messages waiting for workshop managers</div>
      ) : (
        rows.map((r, i) => {
          const rate = ratio(r.groups - r.unanswered, r.groups);
          const c = r.unanswered >= 5 ? "#F04438" : r.unanswered ? "#F79009" : "#17B26A";
          return (
            <button
              key={`${r.key}-${i}`}
              type="button"
              onClick={role === "sa" ? () => onOpenAdvisor(r) : undefined}
              className={`${AN_LI} ${i === 0 ? "!border-t-0" : ""} ${role === "sa" ? "" : "cursor-default"}`}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-msd-soft text-[15px] font-bold text-msd-ink2">
                {first(r.name)}
              </span>
              <span className="min-w-0 grow">
                <b className="block text-[15.5px] font-semibold">
                  {r.name}
                  {r.duplicate ? <span className={`${AN_SUB} font-normal`}> · second account</span> : null}
                </b>
                <span className={AN_SUB}>
                  {Math.round(rate * 100)}% replied · {r.groups} cars
                </span>
              </span>
              <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
                <Ring cx={17} r={13} w={5} frac={rate} color={c} />
              </svg>
              <span className={AN_BIG} style={{ color: r.unanswered ? "var(--ink)" : "var(--faint)" }}>
                {r.unanswered}
              </span>
            </button>
          );
        })
      )}
    </section>
  );
}

export function EscalationCard({ escalation, scopeLabel, onOpen }) {
  const arcs = donutArcs(escalation.reasons);
  return (
    <section className={AN_CARD} data-testid="msd-escalations">
      <div className={AN_H}>
        <div className={AN_T}>Service cases escalated</div>
        <span className={AN_SUB}>{scopeLabel}</span>
      </div>
      <div className={AN_DONUT}>
        <div className="relative h-[160px] w-[160px] shrink-0">
          <svg width="160" height="160" viewBox="0 0 160 160" role="img" aria-label="Escalation reasons">
            {arcs.map((a) => (
              <circle
                key={a.key}
                cx="80"
                cy="80"
                r="58"
                fill="none"
                stroke={ESCALATION_COLORS[a.key]}
                strokeWidth="18"
                strokeDasharray={`${a.dash.toFixed(1)} ${a.circumference.toFixed(1)}`}
                strokeDashoffset={a.offset.toFixed(1)}
                transform="rotate(-90 80 80)"
              />
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className={AN_NUM_SM}>
              {escalation.rate_pct}
              <Unit>%</Unit>
            </div>
            <div className={AN_SUB}>of cars</div>
          </div>
        </div>
        <div className="min-w-0 grow">
          {escalation.reasons.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => onOpen(ESCALATION_LINKS[r.key])}
              className={AN_LI_TIGHT}
            >
              <Dot color={r.count ? ESCALATION_COLORS[r.key] : "var(--line2)"} />
              <span className={`grow text-[14.5px] ${r.count ? "" : "text-msd-faint"}`}>{r.label}</span>
              <span className={`${AN_BIG_SM} ${r.count ? "" : "text-msd-faint"}`}>{r.count}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export const SectionTitle = ({ children, style }) => (
  <div className={AN_SEC} style={style}>
    {children}
  </div>
);

export const Note = ({ children }) => <div className={AN_SUB}>{children}</div>;
